import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  ClientActions,
  IClient,
  selectAllClientsView,
} from '@techspert-io/clients';
import { ToastService } from '@techspert-io/user-alerts';
import { combineLatest, filter, Subject, takeUntil, tap } from 'rxjs';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { EnrichedUser } from '../../models/user.models';
import { UserService } from '../../services/user.service';
import { CreateUserComponent } from '../create-user/create-user.component';
import { EditUserComponent } from '../edit-user/edit-user.component';
import {
  UsersFilterComponent,
  UsersFilterFormValue,
  UserStatus,
} from '../users-filter/users-filter.component';
import { UsersListComponent } from '../users-list/users-list.component';

@Component({
  selector: 'app-user-management-dashboard',
  templateUrl: './user-management-dashboard.component.html',
  styleUrls: ['user-management-dashboard.component.scss'],
  imports: [
    MaterialModule,
    UsersFilterComponent,
    UsersListComponent,
    CreateUserComponent,
    EditUserComponent,
  ],
})
export class UserManagementDashboardComponent implements OnInit, OnDestroy {
  users: EnrichedUser[] = [];
  filteredUsers: EnrichedUser[] = [];
  clients: IClient[] = [];

  filterValue: UsersFilterFormValue;

  selectedUserId: string;
  selectedUser: EnrichedUser;

  isLoading = true;
  createMode = false;

  private destroy$ = new Subject();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private userService: UserService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.handlePathParams();

    this.store.dispatch(ClientActions.fetchAllClients());

    combineLatest([
      this.store.select(selectAllClientsView),
      this.userService.getAll(),
    ])
      .pipe(
        filter(([{ state }]) => state.isLoaded),
        tap(([{ clients }, users]) => {
          this.populateUsers(users);
          this.clients = clients;
          this.isLoading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  refreshUsers(userId?: string) {
    this.userService
      .getAll()
      .pipe(
        tap((users) => {
          this.populateUsers(users);

          if (userId) {
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { userId },
              queryParamsHandling: 'replace',
            });
          }
        })
      )
      .subscribe();
  }

  handleFilterChange(value: UsersFilterFormValue) {
    this.filterValue = value;
    this.filterUsers();
  }

  setCreateMode(createMode: boolean) {
    this.createMode = createMode;
  }

  private populateUsers(users: EnrichedUser[]) {
    this.users = users;
    this.selectUser(this.selectedUserId);
    this.filterUsers();
  }

  private filterUsers() {
    if (!this.filterValue) {
      this.filteredUsers = this.users;
      return;
    }

    this.filteredUsers = this.users.filter(
      (user) =>
        this.selectedUserId === user.user_id ||
        (this.userSearch(user, this.filterValue.search) &&
          this.userClientFilter(user, this.filterValue.clientIds) &&
          this.userGroupFilter(user, this.filterValue.groupIds) &&
          this.userStatusFilter(user, this.filterValue.status))
    );
  }

  private userSearch(user: EnrichedUser, search?: string) {
    const normalizedSearch = search?.toLowerCase();

    return normalizedSearch
      ? `${user.first_name} ${user.last_name}`
          .toLowerCase()
          .includes(normalizedSearch) || user.email.includes(normalizedSearch)
      : true;
  }

  private userClientFilter(user: EnrichedUser, clientIds: string[] = []) {
    return clientIds.length
      ? user.client_ids.some((clientId) => clientIds.includes(clientId))
      : true;
  }

  private userGroupFilter(user: EnrichedUser, groupIds: string[] = []) {
    return groupIds.length
      ? user.user_groups.some(({ user_group_id }) =>
          groupIds.includes(user_group_id)
        )
      : true;
  }

  private userStatusFilter(
    user: EnrichedUser,
    status: UserStatus = UserStatus.Active
  ) {
    if (status === UserStatus.All) {
      return true;
    }

    return status === UserStatus.Active ? user.is_active : !user.is_active;
  }

  private handlePathParams() {
    this.route.queryParamMap
      .pipe(
        tap((params) => {
          const userId = params.get('userId');

          if (!userId) {
            this.selectedUser = undefined;
            this.selectedUserId = undefined;
            return;
          }

          this.selectUser(userId);
        })
      )
      .subscribe();
  }

  private selectUser(userId?: string) {
    if (!userId) {
      return;
    }

    this.setCreateMode(false);

    this.selectedUser = this.users.find(({ user_id }) => user_id === userId);

    if (!this.isLoading && !this.selectedUser) {
      this.toastService.sendMessage(
        'User not found, please select a user from the list',
        'error'
      );

      this.selectedUserId = undefined;

      return;
    }

    this.selectedUserId = userId;
  }
}
